import React, { useState, useEffect } from "react";
import { useApp } from "context";
import { Constants, Enums } from "utils";
import { Custom, Form, Progress } from "components";
import { PairValue } from "types";
import { IGoal } from "interfaces";
import { Grid } from "@mui/material";

type Props = {
  sx?: any;
  opened: boolean;
  onClose?: () => void;
};

export const HealthGoal = (props: Props) => {
  const { t, theme, service } = useApp();

  const [healthGoals, setHealthGoals] = useState<PairValue[]>([]);
  const [patientHealthGoals, setPatientHealthGoals] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedGoal, setSelectedGoal] = useState<number | "">("");
  const [incrementComponent, setIncrementComponent] = useState(0);

  useEffect(() => {
    service.goal.all().then(([status, response]) => {
      let arr = Constants.QubePlus.healthGoals.map((pv) => ({
        key: -10 - pv.key,
        value: pv.value,
      }));

      response.forEach((hg: IGoal) => {
        arr = arr.filter((pv) => pv.value !== hg.healthGoal);
      });

      setHealthGoals(arr);
      setPatientHealthGoals(
        response.map((hg: IGoal) => ({
          id: String(hg.healthGoalId),
          name: hg.healthGoal,
        }))
      );
    });
  }, [incrementComponent]);

  const updateGoals = (phgs: { id: string; name: string }[]) => {
    service.goal
      .update(phgs.map((phg) => phg.name))
      .then((res) => setIncrementComponent(incrementComponent + 1));
  };

  const handleSelectGoal = (key: number) => {
    const newGoal = healthGoals.find((pv) => pv.key === key);
    setSelectedGoal("");

    if (patientHealthGoals.length >= 3) {
      const goalToRemove = patientHealthGoals[2];
      setHealthGoals([
        ...healthGoals.filter((pv) => pv.key !== key),
        { key: Number(goalToRemove.id), value: goalToRemove.name },
      ]);

      const updatedGoals = [
        ...patientHealthGoals.splice(0, 2),
        { id: String(newGoal?.key), name: newGoal?.value },
      ];

      setPatientHealthGoals(updatedGoals);
      updateGoals(updatedGoals);
    } else {
      setHealthGoals(healthGoals.filter((pv) => pv.key !== key));

      const updatedGoals = [
        ...patientHealthGoals,
        { id: String(newGoal?.key), name: newGoal?.value },
      ];

      setPatientHealthGoals(updatedGoals);
      updateGoals(updatedGoals);
    }
  };

  const handleReorder = (startIndex: number, endIndex: number) => {
    // Create a copy of the selectedGoals array
    const updatedGoals = [...patientHealthGoals];

    // Reorder the items in the array
    const [movedItem] = updatedGoals.splice(startIndex, 1);
    updatedGoals.splice(endIndex, 0, movedItem);

    // Update the state with the reordered array
    setPatientHealthGoals(updatedGoals);
    updateGoals(updatedGoals);
  };

  const handleDelete = (id: string) => {
    const goal = patientHealthGoals.find((phg) => phg.id === id);

    setSelectedGoal(Number(id));

    const updatedGoals = patientHealthGoals.filter((phg) => phg.id !== id);

    setPatientHealthGoals(updatedGoals);
    updateGoals(updatedGoals);

    setHealthGoals([
      ...healthGoals,
      { key: Number(goal?.id), value: goal?.name },
    ]);
  };

  return (
    <Custom.Modal
      opened={props.opened}
      onClose={props.onClose}
      header={"My Health Goals"}
    >
      <Grid container spacing={theme.margin.md} sx={{ marginBottom: "3rem" }}>
        <Grid item xs={12} sm={12} md={6}>
          <Custom.Select
            label={"Choose Health Goals"}
            name={"healthGoals"}
            items={healthGoals}
            value={selectedGoal}
            onChange={handleSelectGoal}
          ></Custom.Select>
        </Grid>
        <Grid item xs={0} sm={0} md={6}></Grid>
      </Grid>

      <Grid
        container
        spacing={theme.margin.md}
        sx={{ marginBottom: "3rem", height: "200px" }}
      >
        <Grid item xs={12} sm={12} md={6}>
          <Custom.ReorderList
            items={patientHealthGoals}
            onReorder={handleReorder}
            onDelete={handleDelete}
          />
        </Grid>
        <Grid item xs={0} sm={0} md={6}></Grid>
      </Grid>
    </Custom.Modal>
  );
};
